"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import { useSuggestionsFeatureFlag } from "@/hooks/useSuggestionsFeatureFlag";

/**
 * Single switch for the keyword search field. Production (or flag-off)
 * renders a bare plain <input> — DOM-identical to the pre-autocomplete
 * markup, with zero extra chunks or requests. Non-production with all flags
 * on renders the dynamically-imported autocomplete, so the suggestion chunk
 * is never requested in production.
 *
 * Async-flag safety: SSR and the first client render always take the plain
 * branch (the PostHog flag resolves client-side). The swap only happens while
 * the field is NOT focused, so a late flag load can never steal focus or
 * mismatch hydration. (The reverse swap — flag off while focused — can drop
 * focus; acceptable for an admin kill-switch transition.)
 *
 * Promotion path: delete the plain branch once the rollout completes.
 */

const SearchAutocompleteInput = dynamic(
  () => import("@/components/shared/SearchAutocompleteInput"),
  // No `loading` fallback: the chunk is requested at initial page render in
  // non-production, long before the field can receive focus, so there is no
  // focus-loss or mid-typing flash window. (Production never requests it.)
  { ssr: true },
);

interface KeywordInputProps {
  variant: "desktop" | "mobile";
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputClassName: string;
  ariaLabel?: string;
}

const KeywordInput: React.FC<KeywordInputProps> = ({
  variant,
  value,
  onChange,
  placeholder,
  inputClassName,
  ariaLabel,
}) => {
  const { isProduction } = getRuntimeEnvironment();
  const flag = useSuggestionsFeatureFlag();
  const [focused, setFocused] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);

  const gate =
    !isProduction && process.env.NEXT_PUBLIC_SEARCH_SUGGESTIONS === "on";
  const wantAutocomplete = gate && flag.loaded && flag.enabled;

  // Swap branches only while blurred — never mid-typing.
  useEffect(() => {
    if (!focused) setShowAutocomplete(wantAutocomplete);
  }, [wantAutocomplete, focused]);

  if (!showAutocomplete) {
    return (
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={inputClassName}
      />
    );
  }

  return (
    <SearchAutocompleteInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      suggestionsEnabled={gate && flag.enabled}
      inputClassName={inputClassName}
      ariaLabel={ariaLabel ?? `Search properties (${variant})`}
    />
  );
};

export default KeywordInput;
